import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Form,
  Input,
  Checkbox,
  notification,
  Dropdown,
  Menu,
  Rate,
} from 'antd'
import {
  CopyOutlined,
  MailOutlined,
  YoutubeFilled,
  StarFilled,
  GiftOutlined,
  GlobalOutlined,
  DownOutlined,
} from '@ant-design/icons'
import colozooLogo from '../assets/colozoo_logo.svg'
import PageTitle from './PageTitle'
import PageLayout from './PageLayout'
import { updateMetaTags } from '../utils/metaTagManager'
import { sendEmailFunc } from '../App'
import { subscribeToNewsletter } from '../utils/newsletter'
import {
  candlePensConfig,
  getAmazonReviewUrl,
  getAmazonStoreUrl,
  getYoutubeUrl,
  watchSectionBackgroundStyle,
} from '../data/candlePens'
import './CandlePens.css'

const { TextArea } = Input

interface NewsletterValues {
  email: string
  consent: boolean
}

const languageOptions = [
  { code: 'en-GB', label: 'English (GB)' },
  { code: 'en-US', label: 'English (US)' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
  { code: 'es', label: 'Español' },
]

const CandlePens = () => {
  const { t, i18n } = useTranslation('candlePens')
  const [form] = Form.useForm()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [copied, setCopied] = useState(false)
  const [reviewStars, setReviewStars] = useState(0)
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [feedbackEmail, setFeedbackEmail] = useState('')
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
  const txt = (key: string) => (t(key) as string) || ''

  const language = i18n.resolvedLanguage || i18n.language || 'en-GB'
  const languageCode = language === 'en-US' ? 'EN' : language === 'en-GB' ? 'GB' : language.slice(0, 2).toUpperCase()
  const youtubeUrl = getYoutubeUrl(language)
  const reviewUrl = getAmazonReviewUrl(language)
  const storeUrl = getAmazonStoreUrl(language)
  const showAmazonReview = reviewStars >= 4
  const showPrivateFeedback = reviewStars > 0 && reviewStars < 4

  useEffect(() => {
    updateMetaTags({
      title: txt('meta.title'),
      description: txt('meta.description'),
      url: window.location.href,
    })
  }, [i18n.language, t])

  const copyCoupon = async () => {
    try {
      await navigator.clipboard.writeText(candlePensConfig.couponCode)
      setCopied(true)
      notification.success({ message: t('coupon.copied') })
      window.setTimeout(() => setCopied(false), 2200)
    } catch {
      notification.info({ message: candlePensConfig.couponCode })
    }
  }

  const submitPrivateFeedback = async () => {
    const message = feedbackMessage.trim()
    if (!message) {
      notification.warning({ message: txt('review.messageRequired') })
      return
    }
    setFeedbackSubmitting(true)
    try {
      const adminEmailPayload = {
        email: 'support@colozoo.de',
        subject: 'Colozoo - Candle pens private feedback',
        message: [
          `stars: ${reviewStars}/5`,
          `language: ${language}`,
          'source: /candle-pens',
          feedbackEmail.trim() ? `customerEmail: ${feedbackEmail.trim()}` : 'customerEmail: (not provided)',
          `message: ${message.replace(/\n/g, '<br />')}`,
        ].join('<br />'),
      }
      await sendEmailFunc(JSON.stringify(adminEmailPayload))
      notification.success({
        message: t('review.feedbackSuccessTitle'),
        description: t('review.feedbackSuccessMessage'),
        duration: 8,
      })
      setFeedbackMessage('')
      setFeedbackEmail('')
      setReviewStars(0)
    } catch (error) {
      console.error('Failed to send private feedback:', error)
      notification.error({
        message: t('review.feedbackErrorTitle'),
        description: t('review.feedbackErrorMessage'),
        duration: 6,
      })
    } finally {
      setFeedbackSubmitting(false)
    }
  }

  const onNewsletterFinish = async (values: NewsletterValues) => {
    setIsSubmitting(true)
    try {
      const adminEmailPayload = {
        email: 'support@colozoo.de',
        subject: 'Colozoo - Candle pens newsletter signup',
        message: `email: ${values.email}<br />consent: Agreed<br />language: ${language}<br />source: /candle-pens`,
      }
      await subscribeToNewsletter({
        email: values.email,
        language,
        source: 'candle-pens',
      })
      try {
        await sendEmailFunc(JSON.stringify(adminEmailPayload))
      } catch (emailError) {
        console.error('Newsletter stored, but admin email failed:', emailError)
      }
      notification.success({
        message: t('newsletter.successTitle'),
        description: t('newsletter.successMessage'),
        duration: 8,
      })
      form.resetFields()
    } catch (error) {
      console.error('Failed to submit newsletter signup:', error)
      notification.error({
        message: t('newsletter.errorTitle'),
        description: t('newsletter.errorMessage'),
        duration: 6,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <PageTitle title={txt('meta.title')} />
      <PageLayout>
        <div className="cz-page">
          <div className="cz-panel">
            <Dropdown
              trigger={[ 'click' ]}
              placement="bottomRight"
              overlay={
                <Menu>
                  {languageOptions.map((option) => (
                    <Menu.Item
                      key={option.code}
                      onClick={() => i18n.changeLanguage(option.code)}
                      style={{
                        backgroundColor: i18n.resolvedLanguage === option.code ? '#279063' : 'transparent',
                        color: i18n.resolvedLanguage === option.code ? 'white' : 'inherit',
                        fontWeight: i18n.resolvedLanguage === option.code ? 'bold' : 'normal',
                      }}
                    >
                      {option.label}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <button type="button" className="cz-lang" aria-label="Language">
                <GlobalOutlined className="cz-lang-icon" />
                <span className="cz-lang-code">{languageCode}</span>
                <DownOutlined className="cz-lang-caret" />
              </button>
            </Dropdown>

            <img className="cz-logo" src={colozooLogo} alt="Colozoo" />

            <header className="cz-header">
              <h1>{t('pageTitle')}</h1>
              <p>{t('intro')}</p>
            </header>

            <section className="cz-video" style={watchSectionBackgroundStyle}>
              <div className="cz-video-inner">
                <div className="cz-video-copy">
                  <span className="cz-badge cz-badge-video">
                    <span className="cz-badge-dot" />
                    {t('video.badge')}
                  </span>
                  <div className="cz-video-body">
                    <h2>{t('video.title')}</h2>
                    <p>{t('video.text')}</p>
                    <Button
                      type="primary"
                      size="large"
                      icon={<YoutubeFilled />}
                      href={youtubeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cz-video-btn"
                    >
                      {t('video.button')}
                    </Button>
                  </div>
                  <div className="cz-video-highlights">
                    <span>{t('video.highlight1')}</span>
                    <span>{t('video.highlight2')}</span>
                    <span>{t('video.highlight3')}</span>
                  </div>
                </div>
                <div className="cz-video-visual">
                  <a
                    className="cz-video-thumb"
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={txt('video.button')}
                  >
                    <img
                      src={candlePensConfig.videoScreenshot}
                      alt={txt('video.imageAlt')}
                    />
                    <span className="cz-video-play" aria-hidden="true">
                      <span className="cz-video-play-icon" />
                    </span>
                  </a>
                </div>
              </div>
            </section>

            <section className="cz-dual">
              <article className="cz-subcard">
                <div>
                  <div className="cz-subcard-top">
                    <span className="cz-badge cz-badge-quiet">{t('coupon.badge')}</span>
                    <GiftOutlined style={{ color: '#da2128', fontSize: 18 }} />
                  </div>
                  <h3>{t('coupon.title')}</h3>
                  <p>{t('coupon.text')}</p>
                </div>
                <div>
                  <div className="cz-coupon-box">
                    <span className="cz-coupon-code">{candlePensConfig.couponCode}</span>
                    <Button size="small" icon={<CopyOutlined />} onClick={copyCoupon}>
                      {copied ? t('coupon.copied') : t('coupon.copy')}
                    </Button>
                  </div>
                  <p className="cz-hint">
                    {t('coupon.hintBefore')}
                    <a href={storeUrl} target="_blank" rel="noopener noreferrer">
                      {t('coupon.hintStoreLink')}
                    </a>
                    {t('coupon.hintAfter')}
                  </p>
                </div>
              </article>

              <article className="cz-subcard cz-subcard-review">
                <div>
                  <div className="cz-subcard-top">
                    <span className="cz-stars" aria-hidden="true">
                      <StarFilled /><StarFilled /><StarFilled /><StarFilled /><StarFilled />
                    </span>
                    <span className="cz-badge cz-badge-community">{t('review.badge')}</span>
                  </div>
                  <h3>{t('review.title')}</h3>
                  <p>{t('review.text')}</p>
                  <p className="cz-review-prompt">{t('review.ratingPrompt')}</p>
                  <Rate
                    className="cz-review-rate"
                    value={reviewStars}
                    onChange={setReviewStars}
                  />
                </div>
                {showAmazonReview && (
                  <div className="cz-review-action">
                    <p className="cz-review-hint">{t('review.amazonHint')}</p>
                    <Button
                      block
                      type="primary"
                      icon={<StarFilled style={{ color: '#facc15' }} />}
                      href={reviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('review.amazonButton')}
                    </Button>
                  </div>
                )}
                {showPrivateFeedback && (
                  <div className="cz-review-private">
                    <p className="cz-review-private-title">{t('review.privateTitle')}</p>
                    <p className="cz-review-private-text">{t('review.privateText')}</p>
                    <TextArea
                      rows={3}
                      value={feedbackMessage}
                      onChange={(e) => setFeedbackMessage(e.target.value)}
                      placeholder={txt('review.messagePlaceholder')}
                    />
                    <Input
                      className="cz-review-email"
                      value={feedbackEmail}
                      onChange={(e) => setFeedbackEmail(e.target.value)}
                      placeholder={txt('review.emailOptional')}
                    />
                    <Button
                      block
                      type="primary"
                      loading={feedbackSubmitting}
                      onClick={submitPrivateFeedback}
                    >
                      {feedbackSubmitting ? t('review.sending') : t('review.sendFeedback')}
                    </Button>
                  </div>
                )}
              </article>
            </section>

            <section className="cz-newsletter">
              <h3>{t('newsletter.title')}</h3>
              <p>{t('newsletter.text')}</p>
              <Form
                form={form}
                name="candle_pens_newsletter"
                onFinish={onNewsletterFinish}
                requiredMark={false}
              >
                <div className="cz-newsletter-row">
                  <Form.Item
                    className="cz-email-item"
                    name="email"
                    rules={[
                      { required: true, message: txt('newsletter.emailRequired') },
                      { type: 'email', message: txt('newsletter.emailInvalid') },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder={txt('newsletter.emailPlaceholder')}
                      aria-label={txt('newsletter.emailLabel')}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      loading={isSubmitting}
                    >
                      {isSubmitting ? t('newsletter.submitting') : t('newsletter.submit')}
                    </Button>
                  </Form.Item>
                </div>
                <Form.Item
                  className="cz-consent"
                  name="consent"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(new Error(txt('newsletter.consentRequired'))),
                    },
                  ]}
                >
                  <Checkbox>
                    {t('newsletter.consent')}{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                      {t('footer.privacy')}
                    </a>.
                  </Checkbox>
                </Form.Item>
              </Form>
            </section>

            <div className="cz-help-bottom">
              <div className="cz-help-pill">
                <MailOutlined />
                <span>
                  {t('help.text')}{' '}
                  <a href="mailto:support@colozoo.de">support@colozoo.de</a>
                  . {t('help.after')}
                </span>
              </div>
            </div>

            <nav className="cz-footer">
              <a href="/">{t('footer.home')}</a>
              <span className="cz-footer-sep">|</span>
              <a href="/legal-notice/" target="_blank" rel="noopener noreferrer">{t('footer.legal')}</a>
              <span className="cz-footer-sep">|</span>
              <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer">{t('footer.privacy')}</a>
            </nav>
          </div>
        </div>
      </PageLayout>
    </>
  )
}

export default CandlePens
